/**
 * Content Library API service for videos and presentations
 */

import apiClient, { ApiResponse } from './client';

// ======================
// Segmentation Modes
// ======================
export type SegmentationMode = 'none' | 'auto';
export type PlaylistPolicy = 'mixable' | 'standalone';

// ======================
// Video Types
// ======================
export interface Video {
  video_id: string;
  organization_id: string;
  filename: string;
  title?: string;
  description?: string;
  storage_path?: string;
  storage_url?: string;
  source_dom_recording_url?: string | null;
  source_dom_audio_url?: string | null;
  status: 'pending' | 'pending_segmentation' | 'processing' | 'segmenting' | 'adding_to_collection' | 'ready' | 'failed' | 'error';
  segmentation_mode?: SegmentationMode;
  error_message?: string;
  duration_seconds?: number;
  file_size_bytes?: number;
  mime_type?: string;
  playlist_policy?: PlaylistPolicy | null;
  playlist_group?: string | null;
  created_at: string;
  updated_at: string;
}

// ======================
// Upload Flow Types (3-step signed URL flow)
// ======================
export interface RequestUploadRequest {
  organization_id: string;
  filename: string;
  title?: string;
  playlist_policy?: PlaylistPolicy;
  playlist_group?: string;
}

export interface RequestUploadResponse {
  video_id: string;
  upload_url: string;
  expires_in: number;
}

export interface ConfirmUploadRequest {
  segmentation_mode: SegmentationMode;
  custom_instructions?: string;
  metadata_instructions?: string;
  min_segment_duration?: number;  // default: 5
  max_segment_duration?: number;  // default: 60
  target_segment_count?: number | null;
  generate_thumbnails?: boolean;  // default: true
  playlist_policy?: PlaylistPolicy;
  playlist_group?: string | null;
}

export interface ConfirmUploadResponse {
  video_id: string;
  status: 'processing' | 'pending_segmentation' | 'ready';
  file_size: number;
}

// Legacy upload interface (deprecated - use 3-step flow)
export interface UploadVideoRequest {
  organization_id: string;
  file: File;
  segmentation_mode?: SegmentationMode;
  title?: string;
  description?: string;
  playlist_policy?: PlaylistPolicy;
  playlist_group?: string;
}

export interface UploadPresentationRequest {
  organization_id: string;
  file: File;
  title?: string;
  description?: string;
}

export interface UploadPresentationResponse {
  deck: {
    deck_id: string;
    organization_id: string;
    filename: string;
    title: string;
    description?: string;
    total_slides: number;
    storage_path: string;
    created_at: string;
  };
  slides: Array<{
    slide_id: string;
    deck_id: string;
    slide_number: number;
    title: string;
    content_summary: string;
    key_points: string[];
    visual_description: string;
    topics: string[];
    use_case: string;
    image_url: string;
    created_at: string;
  }>;
}

export interface UploadVideoResponse {
  video_id: string;
  organization_id: string;
  filename: string;
  storage_path: string;
  storage_url: string;
  file_size_bytes: number;
  status: Video['status'];
  playlist_policy?: PlaylistPolicy | null;
  playlist_group?: string | null;
  message: string;
}

export interface Slide {
  id: string;
  deck_id: string;
  slide_number: number;
  image_url: string;
  title: string;
  content_summary: string;
  key_points: string[];
  visual_description: string;
  topics: string[];
  use_case: string;
  created_at: string;
}

export interface PresentationDeck {
  deck_id: string;
  organization_id: string;
  filename: string;
  title?: string;
  description?: string;
  total_slides: number;
  storage_path?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Presentation extends PresentationDeck {
  slides: Slide[];
}

export interface VideosResponse {
  videos: Video[];
}

export interface VideoStatusResponse {
  video_id: string;
  status: 'pending' | 'pending_segmentation' | 'processing' | 'ready' | 'error';
  storage_url: string | null;
  error_message: string | null;
  playlist_policy?: PlaylistPolicy | null;
  playlist_group?: string | null;
}

export interface UpdateVideoPlaylistControlsRequest {
  organization_id: string;
  playlist_policy?: PlaylistPolicy;
  playlist_group?: string | null;
}

export interface PresentationResponse {
  deck: PresentationDeck | null;
  slides: Slide[];
}

export interface Segment {
  segment_id: string;
  start_time: number;
  end_time: number;
  duration: number;
  thumbnail_url?: string;
  metadata?: Record<string, any>;
}

// Segment from the unified /api/videos/{video_id}/segments endpoint
export interface VideoSegment {
  id: string;
  segmentation_id?: string;
  segment_type?: 'auto' | 'custom';
  start_time: number;
  end_time: number;
  thumbnail_url: string | null;
  label: string | null;
  description: string | null;
  tags: string[];
  ai_context: string | null;
  content_type?: string | null;
  // New fields from updated API
  visual_description: string | null;
  speech_transcript: string | null;
  scene_text: string | null;
  custom_metadata?: Record<string, any>;
}

export interface PromptReviewMatch {
  segment_id: string;
  label?: string | null;
  description?: string | null;
  status?: string | null;
  start_time?: number | null;
  end_time?: number | null;
  duration?: number | null;
  thumbnail_url?: string | null;
  video_id?: string | null;
  video_title?: string | null;
}

export interface PromptReviewCandidate {
  segment_id: string;
  segmentation_id?: string | null;
  segment_type?: 'auto' | 'custom' | 'manual' | null;
  start_time: number;
  end_time: number;
  duration: number;
  label?: string | null;
  description?: string | null;
  tags: string[];
  thumbnail_url?: string | null;
  speech_transcript?: string | null;
  recommended_action?: string | null;
  reason?: string | null;
  confidence?: number | null;
  review_status?: string | null;
  review_decision?: string | null;
  matched_segment?: PromptReviewMatch | null;
  custom_metadata: Record<string, any>;
  playlist_group?: string | null;
}

export interface PromptReviewListResponse {
  video_id: string;
  total_candidates: number;
  pending_candidates: number;
  candidates: PromptReviewCandidate[];
  available_groups?: string[];
}

export type PromptReviewDecision = 'approve' | 'replace_matched' | 'library_only' | 'ignore';

export interface PromptReviewDecisionInput {
  segment_id: string;
  decision: PromptReviewDecision;
  target_playlist_group?: string | null;
}

export interface RefreshPromptReviewRequest {
  organization_id: string;
  group_overrides: Record<string, string>;
}

export interface ApplyPromptReviewRequest {
  organization_id: string;
  decisions: PromptReviewDecisionInput[];
}

export interface ApplyPromptReviewResponse {
  updated: number;
  failed: number;
  results: Array<{
    segment_id: string;
    success: boolean;
    decision: PromptReviewDecision;
    demoted_segment_id?: string | null;
    error?: string;
  }>;
}

export interface AllSegmentsResponse {
  video_id?: string;
  total_segments?: number;
  segmentations_count?: number;
  segments: VideoSegment[];
}

// Legacy enhanced segment type (for internal use)
export type EnhancedSegment = VideoSegment;

export interface SegmentMetadataRequest {
  organization_id: string;
  label?: string;
  description?: string;
  tags?: string[];
  ai_context?: string;
  content_type?: string;
  overview_position?: number | null;
  custom_metadata?: Record<string, any>;
}

export interface SegmentMetadataResponse {
  segment_id: string;
  metadata_id: string;
  label?: string;
  description?: string;
  tags: string[];
  custom_metadata: Record<string, any>;
}

export interface GetSegmentMetadataResponse {
  id: string;
  cloudglue_segmentation_id: string;
  cloudglue_segment_id: string;
  segment_type: 'auto' | 'custom';
  label?: string | null;
  description?: string | null;
  tags?: string[];
  ai_context?: string | null;
  content_type?: string | null;
  custom_metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface BulkSegmentMetadataUpdate {
  segment_id: string;
  label?: string;
  description?: string;
  tags?: string[];
  ai_context?: string;
  content_type?: string;
  custom_metadata?: Record<string, any>;
}

export interface BulkUpdateMetadataRequest {
  organization_id: string;
  segments: BulkSegmentMetadataUpdate[];
}

export interface BulkUpdateMetadataResult {
  segment_id: string;
  success: boolean;
  metadata_id?: string;
  error?: string;
}

export interface BulkUpdateMetadataResponse {
  updated: number;
  failed: number;
  results: BulkUpdateMetadataResult[];
}

export interface DeleteMetadataResponse {
  segment_id: string;
  deleted: boolean;
}

export interface GeneratedSegmentMetadata {
  segment_id: string;
  start_time: number;
  end_time: number;
  generated_metadata: {
    label: string;
    description: string;
    tags: string[];
    ai_context: string;
    content_type: 'overview' | 'feature-highlight' | 'tutorial' | 'configuration';
  };
}

export interface GenerateMetadataRequest {
  organization_id: string;
  preview: boolean;
  metadata_instructions?: string;
}

export interface GenerateMetadataResponse {
  segmentation_id: string;
  total_segments: number;
  metadata_generated?: number;
  errors: string[];
  segments?: GeneratedSegmentMetadata[];
}

export interface GenerateClipsRequest {
  organization_id: string;
  overwrite?: boolean;
}

export interface GenerateClipsResponse {
  video_id: string;
  total_segments: number;
  clips_queued: number;
  already_generated: number;
  started: boolean;
  message: string;
}

export interface SegmentationInfo {
  segmentation_id: string;
  status: string;
  segment_type: 'auto' | 'custom';
  total_segments: number;
}

export interface ListSegmentationsResponse {
  total: number;
  segmentations: SegmentationInfo[];
}

export interface DeleteDeckResponse {
  message: string;
  deck_id: string;
  deck_title: string;
  organization_id: string;
  slides_deleted: number;
  images_deleted: number;
  failed_deletions: string[] | null;
}

export interface DeleteVideoResponse {
  message: string;
  video_id: string;
}

// ======================
// Custom Segmentation Types
// ======================
export interface CreateSegmentInput {
  start_time: number;
  end_time: number;
  label?: string;
}

export interface CreateCustomSegmentationRequest {
  organization_id: string;
  segments: CreateSegmentInput[];
  auto_generate_metadata: boolean;
  metadata_instructions?: string;
}

export interface CreateCustomSegmentationResponse {
  id: string;
  organization_video_id: string;
  name: string | null;
  status: string;
  segmentation_type: string;
  total_segments: number;
  segments: Array<Record<string, any>>;
}

export interface ActivateVideoSegment {
  start_time: number;
  end_time: number;
  label?: string;
}

class ContentLibraryService {
  /**
   * Get all videos for an organization
   */
  async getOrganizationVideos(
    organizationId: string,
    status?: Video['status']
  ): Promise<ApiResponse<VideosResponse>> {
    const params = status ? { status } : undefined;
    return apiClient.get<VideosResponse>(`/api/videos/organization/${organizationId}`, params);
  }

  /**
   * Get status of a specific video
   */
  async getVideoStatus(videoId: string): Promise<ApiResponse<VideoStatusResponse>> {
    return apiClient.get<VideoStatusResponse>(`/api/videos/status/${videoId}`);
  }

  /**
   * Delete a video and all associated data
   * This deletes the storage file and database record
   */
  async deleteVideo(videoId: string): Promise<ApiResponse<DeleteVideoResponse>> {
    return apiClient.delete<DeleteVideoResponse>(`/api/videos/${videoId}`);
  }

  /**
   * Update playlist controls for an existing video.
   * This also propagates the controls to existing segments on the backend.
   */
  async updateVideoPlaylistControls(
    videoId: string,
    data: UpdateVideoPlaylistControlsRequest
  ): Promise<ApiResponse<Video>> {
    return apiClient.patch<Video>(`/api/videos/${videoId}/playlist-controls`, data);
  }

  /**
   * Get the active presentation deck for an organization
   */
  async getOrganizationPresentationDeck(
    organizationId: string
  ): Promise<ApiResponse<PresentationResponse>> {
    return apiClient.get<PresentationResponse>(`/api/presentations/${organizationId}/deck`);
  }

  /**
   * Update slide information
   */
  async updateSlide(
    slideId: string,
    updates: {
      title?: string;
      content_summary?: string;
      key_points?: string[];
      visual_description?: string;
      topics?: string[];
      use_case?: string;
    }
  ): Promise<ApiResponse<Slide>> {
    return apiClient.patch<Slide>(`/api/presentations/slides/${slideId}`, updates);
  }

  /**
   * Update deck information
   */
  async updateDeck(
    deckId: string,
    updates: {
      title?: string;
      description?: string;
      is_active?: boolean;
    }
  ): Promise<ApiResponse<PresentationDeck>> {
    return apiClient.patch<PresentationDeck>(`/api/presentations/decks/${deckId}`, updates);
  }

  /**
   * Delete a presentation deck and all its slides
   */
  async deleteDeck(deckId: string): Promise<ApiResponse<DeleteDeckResponse>> {
    return apiClient.delete<DeleteDeckResponse>(`/api/presentations/decks/${deckId}`);
  }

  // ============================================
  // New Video Segments API (with metadata support)
  // ============================================

  /**
   * Get all segments for a video (from all segmentations - auto + custom)
   * This is the recommended endpoint for UI display
   */
  async getAllVideoSegments(
    videoId: string,
    organizationId: string
  ): Promise<ApiResponse<AllSegmentsResponse>> {
    return apiClient.get<AllSegmentsResponse>(
      `/api/videos/${videoId}/segments`,
      { organization_id: organizationId }
    );
  }

  async getVideoPromptReview(
    videoId: string,
    organizationId: string,
  ): Promise<ApiResponse<PromptReviewListResponse>> {
    return apiClient.get<PromptReviewListResponse>(
      `/api/videos/${videoId}/prompt-review`,
      { organization_id: organizationId },
    );
  }

  async applyVideoPromptReview(
    videoId: string,
    data: ApplyPromptReviewRequest,
  ): Promise<ApiResponse<ApplyPromptReviewResponse>> {
    return apiClient.post<ApplyPromptReviewResponse>(
      `/api/videos/${videoId}/prompt-review`,
      data,
    );
  }

  async refreshVideoPromptReview(
    videoId: string,
    data: RefreshPromptReviewRequest,
  ): Promise<ApiResponse<PromptReviewListResponse>> {
    return apiClient.post<PromptReviewListResponse>(
      `/api/videos/${videoId}/prompt-review/refresh`,
      data,
    );
  }

  /**
   * List all segmentations for a video (without segment details)
   */
  async listVideoSegmentations(
    videoId: string,
    organizationId: string
  ): Promise<ApiResponse<ListSegmentationsResponse>> {
    return apiClient.get<ListSegmentationsResponse>(
      `/api/videos/${videoId}/segmentations`,
      { organization_id: organizationId }
    );
  }

  /**
   * Add or update metadata for a single segment
   * Note: Uses PUT to /api/videos/{videoId}/segments/{segmentId}/metadata
   */
  async updateSegmentMetadata(
    videoId: string,
    segmentId: string,
    data: SegmentMetadataRequest
  ): Promise<ApiResponse<SegmentMetadataResponse>> {
    return apiClient.put<SegmentMetadataResponse>(
      `/api/videos/${videoId}/segments/${segmentId}/metadata`,
      data
    );
  }

  /**
   * Bulk update metadata for multiple segments
   */
  async bulkUpdateSegmentMetadata(
    videoId: string,
    segmentationId: string,
    data: BulkUpdateMetadataRequest
  ): Promise<ApiResponse<BulkUpdateMetadataResponse>> {
    return apiClient.patch<BulkUpdateMetadataResponse>(
      `/api/videos/${videoId}/segmentations/${segmentationId}/metadata`,
      data
    );
  }

  /**
   * Delete metadata for a segment
   */
  async deleteSegmentMetadata(
    videoId: string,
    segmentationId: string,
    segmentId: string,
    organizationId: string
  ): Promise<ApiResponse<DeleteMetadataResponse>> {
    return apiClient.delete<DeleteMetadataResponse>(
      `/api/videos/${videoId}/segmentations/${segmentationId}/segments/${segmentId}/metadata`,
      { organization_id: organizationId }
    );
  }

  /**
   * Get metadata for a specific segment
   */
  async getSegmentMetadata(
    videoId: string,
    segmentationId: string,
    segmentId: string,
    organizationId: string
  ): Promise<ApiResponse<GetSegmentMetadataResponse>> {
    return apiClient.get<GetSegmentMetadataResponse>(
      `/api/videos/${videoId}/segmentations/${segmentationId}/segments/${segmentId}/metadata`,
      { organization_id: organizationId }
    );
  }

  /**
   * Generate AI metadata for all segments in a segmentation
   * @param preview - If true, returns generated metadata without saving. If false, saves to database.
   */
  async generateMetadata(
    videoId: string,
    segmentationId: string,
    data: GenerateMetadataRequest
  ): Promise<ApiResponse<GenerateMetadataResponse>> {
    return apiClient.post<GenerateMetadataResponse>(
      `/api/videos/${videoId}/segmentations/${segmentationId}/generate-metadata`,
      data
    );
  }

  /**
   * Create a custom segmentation with user-defined segment boundaries.
   * Returns immediately; processing runs in background.
   */
  async createCustomSegmentation(
    videoId: string,
    data: CreateCustomSegmentationRequest
  ): Promise<ApiResponse<CreateCustomSegmentationResponse>> {
    return apiClient.post<CreateCustomSegmentationResponse>(
      `/api/videos/${videoId}/segmentations`,
      data
    );
  }

  /**
   * Generate or backfill per-segment MP4 clip assets for an existing video.
   * Returns immediately; processing continues in the background.
   */
  async generateVideoClips(
    videoId: string,
    data: GenerateClipsRequest
  ): Promise<ApiResponse<GenerateClipsResponse>> {
    return apiClient.post<GenerateClipsResponse>(
      `/api/videos/${videoId}/generate-clips`,
      data
    );
  }

  /**
   * Activate a pending_segmentation video with user-defined segments
   */
  async activateVideo(
    videoId: string,
    data: { segments: ActivateVideoSegment[] }
  ): Promise<ApiResponse<{ message: string }>> {
    return apiClient.post(`/api/videos/${videoId}/activate`, data);
  }

  /**
   * Delete a custom segmentation and its segments
   * Note: Backend endpoint not yet implemented
   */
  async deleteCustomSegmentation(
    videoId: string,
    segmentationId: string,
    organizationId: string
  ): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete(`/api/videos/${videoId}/segmentations/${segmentationId}`, {
      organization_id: organizationId,
    });
  }

  /**
   * Delete a single segment from a segmentation
   */
  async deleteSegment(
    videoId: string,
    segmentationId: string,
    segmentId: string,
    organizationId: string
  ): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete(
      `/api/videos/${videoId}/segmentations/${segmentationId}/segments/${segmentId}`,
      { organization_id: organizationId }
    );
  }

  // ============================================
  // Video Upload API (3-step signed URL flow)
  // ============================================

  /**
   * Step 1: Request a signed upload URL
   * Returns a video_id and upload_url for direct file upload
   */
  async requestUpload(
    data: RequestUploadRequest
  ): Promise<ApiResponse<RequestUploadResponse>> {
    return apiClient.post<RequestUploadResponse>('/api/videos/request-upload', data);
  }

  /**
   * Step 2: Upload file directly to the signed URL
   * This is a direct PUT to the storage provider (not through our API)
   */
  async uploadToSignedUrl(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Step 3: Confirm upload and start processing
   * Choose segmentation mode and optional parameters
   */
  async confirmUpload(
    videoId: string,
    data: ConfirmUploadRequest
  ): Promise<ApiResponse<ConfirmUploadResponse>> {
    return apiClient.post<ConfirmUploadResponse>(
      `/api/videos/confirm-upload/${videoId}`,
      data
    );
  }

  /**
   * @deprecated Use the 3-step flow: requestUpload -> uploadToSignedUrl -> confirmUpload
   * Legacy upload method - uploads file directly through the API
   */
  async uploadVideo(
    data: UploadVideoRequest
  ): Promise<ApiResponse<UploadVideoResponse>> {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('organization_id', data.organization_id);
    if (data.segmentation_mode) {
      formData.append('segmentation_mode', data.segmentation_mode);
    }
    if (data.title) {
      formData.append('title', data.title);
    }
    if (data.description) {
      formData.append('description', data.description);
    }
    if (data.playlist_policy) {
      formData.append('playlist_policy', data.playlist_policy);
    }
    if (data.playlist_group) {
      formData.append('playlist_group', data.playlist_group);
    }

    return apiClient.uploadFile<UploadVideoResponse>('/api/videos/upload', data.file, {
      organization_id: data.organization_id,
      segmentation_mode: data.segmentation_mode,
      title: data.title,
      description: data.description,
      playlist_policy: data.playlist_policy,
      playlist_group: data.playlist_group,
    });
  }

  // ============================================
  // Presentation Upload API
  // ============================================

  /**
   * Upload a new presentation (PPTX or PDF)
   * Note: This is a long-running request - the backend processes all slides
   * with Gemini Vision before returning.
   */
  async uploadPresentation(
    data: UploadPresentationRequest
  ): Promise<ApiResponse<UploadPresentationResponse>> {
    return apiClient.uploadFile<UploadPresentationResponse>('/api/presentations/upload', data.file, {
      organization_id: data.organization_id,
      title: data.title,
      description: data.description,
    });
  }
}

const contentLibraryService = new ContentLibraryService();
export default contentLibraryService;
